import { CustomerDto } from 'api/generated';
import { AppConfig } from 'config';
import { useState } from 'react';
import io from 'socket.io-client';
import { actions, selectors, useAppDispatch, useAppSelector } from 'store';

interface Props {
  errorMessage?: string | null;
}

export const useStripeSocket = ({ errorMessage }: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  const dispatch = useAppDispatch();
  const token = useAppSelector(selectors.auth.selectAccessToken);

  const onPaymentMethodChange = () => {
    setIsLoading(true);

    const socket = io(`${AppConfig.REACT_APP_WS_API_URL}/customer`, {
      transports: ['websocket', 'polling'],
      transportOptions: {
        websocket: {
          extraHeaders: {
            Authorization: `Bearer ${token}`,
          },
        },
        polling: {
          extraHeaders: {
            Authorization: `Bearer ${token}`,
          },
        },
      },
      auth: {
        token: `Bearer ${token}`,
      },
      query: {
        token: `Bearer ${token}`,
      },
    });

    socket.connect();

    const cleanup = () => {
      socket.off('me');
      socket.disconnect();
    };

    socket.on('me', (user: CustomerDto) => {
      // Update user immediately with data from WebSocket
      dispatch(actions.user.setUser(user));

      setIsSuccess(Boolean(user.paymentMethod && !errorMessage));
      setIsLoading(false);
      setIsCompleted(true);

      // Clean up socket after successful update
      cleanup();
    });

    // Set a timeout to clean up the socket if no response is received
    const timeout = setTimeout(() => {
      cleanup();
      setIsLoading(false);
    }, 10000); // 10 second timeout

    socket.on('disconnect', () => {
      clearTimeout(timeout);
      setIsLoading(false);
    });

    socket.on('connect_error', () => {
      cleanup();
      clearTimeout(timeout);
      setIsLoading(false);
    });
  };

  return {
    onPaymentMethodChange,
    isLoadingSocket: isLoading,
    isSuccessSocket: isSuccess,
    isCompletedSocket: isCompleted,
  };
};
